import React, { useEffect, useRef } from 'react';

const LocationModal = ({ isOpen, onClose, ponto }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapLoading, setMapLoading] = React.useState(true);

  const { localizacao, tipo, createdAt } = ponto || {};
  const { latitude, longitude } = localizacao || {};

  // Função para formatar data
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Função para formatar hora
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Função para obter o tipo de ponto
  const getTipoPonto = (tipo) => {
    const tipos = {
      'batida': 'Batida de Ponto',
      'viagem-inicio': 'Início de Viagem',
      'viagem-fim': 'Fim de Viagem'
    };
    return tipos[tipo] || tipo;
  };

  // URL do Google Maps (mantido como opção)
  const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;

  // Inicializar mapa Leaflet
  useEffect(() => {
    if (isOpen && ponto && mapRef.current && !mapInstanceRef.current && latitude && longitude) {
      // Carregar Leaflet dinamicamente
      const loadLeaflet = async () => {
        // CSS do Leaflet
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        link.crossOrigin = '';
        document.head.appendChild(link);

        // JS do Leaflet
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        script.crossOrigin = '';
        
        script.onload = () => {
          try {
            // Inicializar mapa
            const map = window.L.map(mapRef.current).setView([latitude, longitude], 16);
            
            // Adicionar tiles do OpenStreetMap
            window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
              attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
              maxZoom: 19
            }).addTo(map);

            // Adicionar marcador personalizado
            const customIcon = window.L.divIcon({
              className: 'custom-marker',
              html: `
                <div style="
                  background: #5E17EB;
                  width: 30px;
                  height: 30px;
                  border-radius: 50%;
                  border: 3px solid white;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: white;
                  font-size: 16px;
                ">
                  📍
                </div>
              `,
              iconSize: [30, 30],
              iconAnchor: [15, 15]
            });

            window.L.marker([latitude, longitude], { icon: customIcon })
              .addTo(map)
              .bindPopup(`
                <div style="text-align: center; min-width: 200px;">
                  <strong style="color: #5E17EB;">📍 Local do Ponto</strong><br>
                  <small>${formatDate(createdAt)} - ${formatTime(createdAt)}</small><br>
                  <small style="background: #f3f4f6; padding: 2px 6px; border-radius: 4px; display: inline-block; margin-top: 4px;">
                    ${getTipoPonto(tipo)}
                  </small>
                </div>
              `)
              .openPopup();

            // Adicionar círculo para mostrar precisão
            window.L.circle([latitude, longitude], {
              color: '#5E17EB',
              fillColor: '#5E17EB',
              fillOpacity: 0.1,
              radius: 50 // 50 metros de raio
            }).addTo(map);

            mapInstanceRef.current = map;
            setMapLoading(false);
          } catch (error) {
            console.error('Erro ao inicializar mapa:', error);
            setMapLoading(false);
          }
        };

        document.head.appendChild(script);
      };

      loadLeaflet();
    }

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [isOpen, latitude, longitude, createdAt, tipo, ponto]);

  if (!isOpen || !ponto) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900">
              Localização do Ponto
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Informações do Ponto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="text-sm text-gray-500">Data</p>
                <p className="font-medium text-gray-900">{formatDate(createdAt)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-gray-500">Horário</p>
                <p className="font-medium text-gray-900">{formatTime(createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Tipo de Ponto */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-1">Tipo de Registro</p>
            <p className="font-medium text-gray-900">{getTipoPonto(tipo)}</p>
          </div>

          {/* Coordenadas */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-2">Coordenadas GPS</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-400">Latitude</p>
                <p className="font-mono text-sm text-gray-900">{latitude}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Longitude</p>
                <p className="font-mono text-sm text-gray-900">{longitude}</p>
              </div>
            </div>
          </div>

          {/* Mapa */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <p className="text-sm text-gray-500">Localização no Mapa</p>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  OpenStreetMap
                </span>
              </div>
              <div className="flex items-center space-x-2 text-xs text-gray-400">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span>Local exato do ponto</span>
              </div>
            </div>
            <div className="relative">
              <div 
                ref={mapRef}
                id="map"
                className="rounded-lg shadow-lg h-80 sm:h-96"
                style={{ width: '100%' }}
              />
              {mapLoading && (
                <div className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center z-[999]">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Carregando mapa...</p>
                  </div>
                </div>
              )}
              <div className="absolute top-3 right-3 z-[1000]">
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg shadow-md text-sm font-medium transition-colors flex items-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Abrir no Google Maps</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Fechar
          </button>
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Ver no Google Maps</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default LocationModal;
